import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ObjectId } from 'bson';
import {
  useFinancingAcceptOfferApi,
  useFinancingPartnersListApi,
} from '@hero/api-hooks';
import {
  Badge,
  Button,
  LenderChip,
  Price,
  type LenderDisplay,
} from '@hero/web-components';
import {
  AlertCircleIcon,
  CircleCheckIcon,
  SupportIcon,
} from '@hero/web-icons';
import { cn } from '@hero/web-lib';
import type { ApplicationMock, OfferMock } from '../mocks';
import {
  formatOfferFeePercentage,
  formatOfferRepaymentDate,
  formatOfferTerm,
} from '../utils/offerDisplay';
import { resolvePilotLenderLogoUrl } from '../utils/lenderLogo';
import { AcceptOfferDialog } from './AcceptOfferDialog';
import { ContactLenderDialog } from './ContactLenderDialog';
import { MarkAsPaidDrawer } from './MarkAsPaidDrawer';
import { OfferDetailsDialog } from './OfferDetailsDialog';
import { StatusBadge } from './StatusBadge';

export type ApplicationRowDetailsProps = {
  application: ApplicationMock;
};

export function ApplicationRowDetails({ application }: ApplicationRowDetailsProps) {
  const { t } = useTranslation('m-financing');
  const partnersQuery = useFinancingPartnersListApi();
  const acceptOffer = useFinancingAcceptOfferApi();
  const [confirmOffer, setConfirmOffer] = useState<OfferMock | null>(null);
  const [contactOffer, setContactOffer] = useState<OfferMock | null>(null);
  const [detailsOffer, setDetailsOffer] = useState<OfferMock | null>(null);
  const [markAsPaidOpen, setMarkAsPaidOpen] = useState(false);

  /**
   * The carrier can pick an offer only when:
   *  - the application has at least one offered offer (lender sent
   *    terms), AND
   *  - the carrier hasn't already locked in an offer (none of the
   *    rows are `decision: 'accepted'` yet).
   *
   * Once one row flips to `accepted`, the backend transitions the
   * application status to `accepted`. Other `offered` rows stay
   * visible but their CTAs collapse to a read-only state — the
   * carrier can still see who else offered in case the chosen
   * lender later falls through.
   */
  const acceptedOffer = application.offers.find(o => o.decision === 'accepted');
  const isLocked = Boolean(acceptedOffer);

  const handleConfirmAccept = async (offer: OfferMock) => {
    try {
      await acceptOffer.mutateAsync({
        _id: new ObjectId(application.id),
        offerId: offer.id,
      });
      toast.success(
        t('offer_card.accept.success_toast', { lender: offer.lenderName }),
      );
      setConfirmOffer(null);
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : t('offer_card.accept.error_toast'),
      );
    }
  };

  /**
   * Build a `lenderId` → `LenderDisplay` lookup from the active-
   * lenders catalog. Offers carry the canonical `lenderId` (hex
   * `Lender._id`) on the snapshot, and the legacy
   * `/financing/partners` endpoint projects each `Lender` with
   * `id: lender._id.toHexString()` — so `offer.lenderId` matches
   * `partner.id` directly. Cheap memo keyed on the partners list
   * — the catalog is tiny (≤ a handful of pilot partners) so a
   * hashmap per render is well within budget.
   */
  const partnerByKey = useMemo(() => {
    const map = new Map<string, LenderDisplay>();
    for (const p of partnersQuery.data?.partners ?? []) {
      const display: LenderDisplay = {
        id: p.id,
        name: p.name,
        tagline: p.tagline,
        logoUrl: resolvePilotLenderLogoUrl(p.slug, p.logoUrl),
        brandColor: p.brandColor,
        website: p.website,
      };
      map.set(p.id, display);
      map.set(p.slug, display);
    }
    return map;
  }, [partnersQuery.data]);

  return (
    <div className="bg-gray-50/60 px-4 py-5 sm:px-6">
      <h4 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
        {t('applications_table.lenders_heading')}
      </h4>

      {/**
       * Rejection banner — surfaces when a lender (or admin)
       * bounced the carrier's previous repayment receipt. We only
       * mount this when the application is back in `funded` AND
       * `lastRejectionNotes` is set; the adapter clears it the
       * moment a new receipt lands (so the carrier doesn't keep
       * seeing the stale reason after they fix it). The carrier
       * can re-open the Mark-as-paid drawer below to retry.
       */}
      {application.status === 'funded' && application.lastRejectionNotes && (
        <div className="mb-4 flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 sm:p-5">
          <AlertCircleIcon className="mt-0.5 size-4 flex-shrink-0 text-amber-600" />
          <div className="flex flex-col gap-1">
            <p className="text-sm font-semibold text-amber-900">
              {t('repayment_status.rejected.title')}
            </p>
            <p className="text-xs text-amber-800">
              {t('repayment_status.rejected.description')}
            </p>
            <p className="mt-1 whitespace-pre-wrap rounded-md bg-white/60 p-2 text-xs text-amber-900">
              {application.lastRejectionNotes}
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {application.offers.map(offer => {
          const partner =
            (offer.lenderId && partnerByKey.get(offer.lenderId)) || undefined;
          const isAccepted =
            offer.decision === 'accepted' ||
            (acceptedOffer?.id === offer.id);
          const hasTerms =
            offer.decision === 'offered' || offer.decision === 'accepted';
          /**
           * The accepted card is the only one that doubles as a
           * read-only "what did I agree to?" affordance. Clicking
           * anywhere on the card opens {@link OfferDetailsDialog}
           * — we keep the inner button-style interactives (`Mark
           * as paid`, `Contact …`) on `stopPropagation` so a click
           * on those doesn't double-open the details modal.
           */
          const isClickable = isAccepted && Boolean(offer.terms);
          const handleCardClick = () => {
            if (isClickable) setDetailsOffer(offer);
          };
          const handleCardKey = (e: React.KeyboardEvent<HTMLElement>) => {
            if (!isClickable) return;
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setDetailsOffer(offer);
            }
          };

          return (
            <article
              key={offer.id}
              role={isClickable ? 'button' : undefined}
              tabIndex={isClickable ? 0 : undefined}
              onClick={isClickable ? handleCardClick : undefined}
              onKeyDown={isClickable ? handleCardKey : undefined}
              className={cn(
                'rounded-2xl border bg-white p-4 transition-all',
                isAccepted
                  ? 'border-emerald-200 ring-1 ring-emerald-100'
                  : 'border-gray-200',
                isClickable
                  ? 'cursor-pointer hover:shadow-md hover:ring-emerald-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400'
                  : 'hover:shadow-sm',
              )}>
              <div className="flex items-start justify-between gap-3">
                {partner ? (
                  <LenderChip lender={partner} size="sm" />
                ) : (
                  <span className="text-sm font-semibold text-gray-900">
                    {offer.lenderName}
                  </span>
                )}
                <div className="flex flex-col items-end gap-1">
                  {/**
                   * Override the default offer-decision tone for
                   * the `accepted` value — the OFFER_TONE map uses
                   * sky for accepted (consistent with the
                   * application status), but on the carrier-facing
                   * card we want the "you picked this one" badge
                   * to read as green so it visually pairs with the
                   * green-bordered card and the green CTA below.
                   */}
                  <StatusBadge
                    kind="offer"
                    value={offer.decision}
                    className={
                      offer.decision === 'accepted'
                        ? 'bg-emerald-50 text-emerald-800 ring-1 ring-inset ring-emerald-200'
                        : undefined
                    }
                  />
                </div>
              </div>

              {offer.decision === 'pending' && (
                <p className="mt-3 text-sm text-gray-600">
                  {t('offer_card.pending_description', {
                    lender: partner?.name ?? offer.lenderName,
                  })}
                </p>
              )}

              {offer.decision === 'interested' && (
                <div className="mt-3 space-y-3">
                  <p className="text-sm text-gray-700">
                    {t('offer_card.interested_description', {
                      lender: partner?.name ?? offer.lenderName,
                    })}
                  </p>
                  {/**
                   * Hide the Contact CTA on every NON-accepted row
                   * once the carrier has locked in a winner — runner-
                   * up lenders shouldn't get pinged after the deal
                   * has moved on. The accepted card still keeps its
                   * own Contact button in the terms block below.
                   */}
                  {!isLocked && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="gap-1.5"
                      onClick={e => {
                        e.stopPropagation();
                        setContactOffer(offer);
                      }}>
                      <SupportIcon className="size-3.5 shrink-0" />
                      {t('offer_card.contact.cta', {
                        lender: partner?.name ?? offer.lenderName,
                      })}
                    </Button>
                  )}
                </div>
              )}

              {hasTerms && offer.terms && (
                <>
                <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  <div>
                    <dt className="text-xs text-gray-500">
                      {t('offer_card.fields.advanced_amount')}
                    </dt>
                    <dd className="mt-0.5">
                      <Price
                        amount={offer.terms.advancedAmount}
                        currency={application.invoice.currency}
                        size="lg"
                        className="font-bold text-emerald-600"
                      />
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-gray-500">{t('offer_card.fields.fee')}</dt>
                    <dd className="mt-0.5 font-semibold text-gray-900">
                      <Price
                        amount={offer.terms.feeAmount}
                        currency={application.invoice.currency}
                      />
                      <span className="ms-1 text-xs font-normal text-gray-500">
                        ({formatOfferFeePercentage(offer.terms.feePercentage)})
                      </span>
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-gray-500">{t('offer_card.fields.term')}</dt>
                    <dd className="mt-0.5 text-sm font-medium text-gray-900">
                      {formatOfferTerm(t, offer)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-gray-500">
                      {t('offer_card.fields.repayment_date')}
                    </dt>
                    <dd className="mt-0.5 text-sm font-medium text-gray-900">
                      {formatOfferRepaymentDate(offer.terms.repaymentDate)}
                    </dd>
                  </div>
                  {offer.terms.notes && (
                    <div className="col-span-2 mt-1 rounded-lg bg-gray-50 p-2.5">
                      <dt className="text-xs text-gray-500">
                        {t('offer_card.fields.notes')}
                      </dt>
                      <dd className="mt-0.5 text-xs leading-relaxed text-gray-700">
                        {offer.terms.notes}
                      </dd>
                    </div>
                  )}
                </dl>
                <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
                  <div className="flex flex-wrap items-center gap-2">
                    {offer.decision === 'accepted' ? (
                      /**
                       * For the accepted card, the bottom-left
                       * slot rotates with the lifecycle:
                       *  - `funded`: prominent "Mark as paid"
                       *    button → opens {@link MarkAsPaidDrawer}.
                       *  - `awaiting_repayment_approval`: amber
                       *    "Awaiting review" pill so the carrier
                       *    knows they don't need to upload again.
                       *  - `repaid`: green "Repaid" pill — the loop
                       *    is closed.
                       *  - Anything earlier (`accepted` waiting on
                       *    the lender to fund): a softer "Awaiting
                       *    funding" hint so the carrier doesn't
                       *    poke the Mark-as-paid CTA before any
                       *    funds have landed.
                       */
                      application.status === 'funded' ? (
                        <Button
                          type="button"
                          size="sm"
                          onClick={e => {
                            e.stopPropagation();
                            setMarkAsPaidOpen(true);
                          }}>
                          {t('offer_card.mark_as_paid.cta')}
                        </Button>
                      ) : application.status === 'awaiting_repayment_approval' ? (
                        <Badge
                          variant="secondary"
                          size="small"
                          className="bg-amber-50 text-amber-800 ring-1 ring-inset ring-amber-200">
                          {t('offer_card.mark_as_paid.awaiting_badge')}
                        </Badge>
                      ) : application.status === 'repaid' ? (
                        <Badge
                          variant="secondary"
                          size="small"
                          className="gap-1 bg-emerald-50 text-emerald-800 ring-1 ring-inset ring-emerald-200">
                          <CircleCheckIcon className="size-3" />
                          {t('offer_card.mark_as_paid.repaid_badge')}
                        </Badge>
                      ) : (
                        /**
                         * "Live" indicator — pulsing amber dot
                         * sitting next to the awaiting-funding
                         * copy. Same pattern as standard "online"
                         * pills: a static dot in the center with
                         * an `animate-ping` halo so the carrier
                         * reads the state as ACTIVE (lender is
                         * working on it) rather than dormant text.
                         * Amber tone pairs with the other waiting
                         * states (`awaiting_repayment_approval`).
                         */
                        <span className="inline-flex items-center gap-2 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-800 ring-1 ring-inset ring-amber-200">
                          <span
                            className="relative flex size-2"
                            aria-hidden="true">
                            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-75" />
                            <span className="relative inline-flex size-2 rounded-full bg-amber-500" />
                          </span>
                          {t('offer_card.mark_as_paid.awaiting_funding')}
                        </span>
                      )
                    ) : isLocked ? (
                      <span className="text-xs text-gray-500">
                        {t('offer_card.accept.other_locked')}
                      </span>
                    ) : (
                      <Button
                        type="button"
                        size="sm"
                        onClick={e => {
                          e.stopPropagation();
                          setConfirmOffer(offer);
                        }}
                        disabled={acceptOffer.isPending}>
                        {t('offer_card.accept.cta')}
                      </Button>
                    )}
                  </div>
                  {/**
                   * Once any offer is accepted, only the accepted
                   * card keeps its Contact button — runner-up
                   * lenders no longer get a contact CTA because the
                   * carrier has already chosen their counterpart
                   * and we don't want to encourage out-of-band
                   * negotiation on a closed deal.
                   */}
                  {(isAccepted || !isLocked) && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="gap-1.5 text-gray-600 hover:text-gray-900"
                      onClick={e => {
                        e.stopPropagation();
                        setContactOffer(offer);
                      }}>
                      <SupportIcon className="size-3.5 shrink-0 text-gray-500" />
                      {t('offer_card.contact.cta', {
                        lender: partner?.name ?? offer.lenderName,
                      })}
                    </Button>
                  )}
                </div>
                </>
              )}

              {offer.decision === 'declined' && (
                <div className="mt-4 space-y-2">
                  <div>
                    <dt className="text-xs text-gray-500">
                      {t('offer_card.fields.decline_reason')}
                    </dt>
                    <dd className="mt-0.5 text-sm font-medium text-gray-900">
                      {offer.declineReason
                        ? t(`decline_reason.${offer.declineReason}`)
                        : '—'}
                    </dd>
                  </div>
                  {offer.declineNotes && (
                    <div className="rounded-lg bg-gray-50 p-2.5">
                      <p className="text-xs leading-relaxed text-gray-700">
                        {offer.declineNotes}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </article>
          );
        })}
      </div>

      {/**
       * The repayment-receipt upload UX moved into
       * {@link MarkAsPaidDrawer}, opened from the accepted offer
       * card. We keep a single banner here for the post-submission
       * lifecycle states (awaiting review / repaid) so the carrier
       * still gets a top-level cue, but the inline file picker is
       * gone — every receipt write now flows through the modal.
       */}
      {application.status === 'awaiting_repayment_approval' && (
        <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm sm:p-5">
          <p className="font-semibold text-amber-900">
            {t('repayment_status.awaiting.title')}
          </p>
          <p className="mt-0.5 text-xs text-amber-800">
            {t('repayment_status.awaiting.description')}
          </p>
        </div>
      )}
      {application.status === 'repaid' && (
        <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm sm:p-5">
          <p className="flex items-center gap-1.5 font-semibold text-emerald-900">
            <CircleCheckIcon className="size-4" />
            {t('repayment_status.repaid.title')}
          </p>
          <p className="mt-0.5 text-xs text-emerald-800">
            {t('repayment_status.repaid.description')}
          </p>
        </div>
      )}

      <AcceptOfferDialog
        offer={confirmOffer}
        application={application}
        partner={
          confirmOffer?.lenderId
            ? partnerByKey.get(confirmOffer.lenderId)
            : undefined
        }
        isSubmitting={acceptOffer.isPending}
        onCancel={() => setConfirmOffer(null)}
        onConfirm={() => {
          if (confirmOffer) {
            void handleConfirmAccept(confirmOffer);
          }
        }}
      />

      <ContactLenderDialog
        applicationId={application.id}
        offer={contactOffer}
        lenderName={
          contactOffer
            ? (partnerByKey.get(contactOffer.lenderId ?? '')?.name ??
              contactOffer.lenderName)
            : ''
        }
        onClose={() => setContactOffer(null)}
      />

      {/**
       * Read-only "what did I agree to?" modal — opened by
       * clicking the accepted offer card anywhere outside the
       * Mark-as-paid / Contact CTAs.
       */}
      <OfferDetailsDialog
        offer={detailsOffer}
        application={application}
        partner={
          detailsOffer?.lenderId
            ? partnerByKey.get(detailsOffer.lenderId)
            : undefined
        }
        onClose={() => setDetailsOffer(null)}
      />

      {/**
       * Carrier-side "I paid the lender" upload drawer. Posts to
       * `submitRepaymentReceiptAPI` which transitions the
       * application to `awaiting_repayment_approval` for the
       * admin / lender to review. Lives in a drawer (not a
       * dialog) so the proof upload, anti-fraud notice, and the
       * long acknowledgement have room to breathe — same pattern
       * as {@link FinancingProfileDrawer} on this page.
       */}
      <MarkAsPaidDrawer
        isOpen={markAsPaidOpen}
        application={application}
        acceptedOffer={acceptedOffer}
        partner={
          acceptedOffer?.lenderId
            ? partnerByKey.get(acceptedOffer.lenderId)
            : undefined
        }
        onClose={() => setMarkAsPaidOpen(false)}
      />
    </div>
  );
}
